import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { IS_CONFIGURED } from '@/amplify-config'
import { probeDbReady, api } from '@/services/api'

export type UserRole = 'admin' | 'bd_rep' | 'executive'

export type AuthUser = {
  sub: string
  email: string
  firstName: string
  lastName: string
  role: UserRole
  /** True for Microsoft-SSO (federated) sign-ins — they have no Cognito
   *  password, so password UI must not be offered to them. */
  isFederated: boolean
}

type AuthContextValue = {
  user: AuthUser | null
  /** The current user's effective permission keys (role → permission grants,
   *  fetched from GET /permissions/me). `null` until loaded / signed out. */
  permissions: string[] | null
  /** Capability check used to gate UI. Derives from `permissions`; the backend
   *  is the real enforcement — this only decides what to render. In mock/unconfigured
   *  mode it returns true (the mock user is an admin). */
  can: (key: string) => boolean
  loading: boolean
  /** False until the database has answered at least once after sign-in. Holds
   *  the loading screen through an overnight Aurora scale-to-zero resume so the
   *  app shell never renders against a paused DB. See probeDbReady(). */
  dbReady: boolean
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  getAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const MOCK_MODE = !IS_CONFIGURED

const MOCK_USER: AuthUser = {
  sub: 'mock-user-001',
  email: 'kenop@penserna.com',
  firstName: 'Ken',
  lastName: 'O.',
  role: 'admin',
  isFederated: false,
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [permissions, setPermissions] = useState<string[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [dbReady, setDbReady] = useState(MOCK_MODE)
  // Probe once per app load — token refreshes (Hub) re-run loadUser, but the
  // DB is already warm by then and we don't want to re-gate the UI.
  const dbProbed = useRef(false)

  async function ensureDbReady() {
    if (dbProbed.current) return
    dbProbed.current = true
    try {
      await probeDbReady() // resolves once the DB answers (or the window lapses)
    } finally {
      // Proceed regardless: on a timeout the app renders and its own calls
      // surface real errors — never trap the user on the spinner forever.
      setDbReady(true)
    }
  }

  async function loadUser() {
    if (MOCK_MODE) {
      setUser(MOCK_USER)
      setLoading(false)
      return
    }
    try {
      const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth')
      const current = await getCurrentUser()
      const session = await fetchAuthSession()
      const claims = session.tokens?.idToken?.payload ?? {}
      // Federated (Microsoft) users carry their name in standard OIDC claims
      // (`given_name` / `family_name`), while admin-created native users carry
      // it in our custom attributes (`custom:firstName` / `custom:lastName`).
      // Prefer the custom ones (those are what admin sets explicitly) but
      // fall through to standard claims so federated users get a name too.
      setUser({
        sub: current.userId,
        email: String(claims['email'] ?? ''),
        firstName: String(claims['custom:firstName'] ?? claims['given_name'] ?? ''),
        lastName: String(claims['custom:lastName'] ?? claims['family_name'] ?? ''),
        role: String(claims['custom:role'] ?? 'bd_rep') as UserRole,
        // Cognito sets `identities` on the id token only for federated users.
        isFederated: Boolean(claims['identities']),
      })
      // Signed in — hold the loader until the DB is awake (fire-and-forget;
      // dbReady flips when the probe resolves).
      void ensureDbReady()
      // Load the caller's effective permissions for UI gating (fire-and-forget;
      // `can()` returns false for a key until this resolves). Failure → deny.
      void api
        .get<{ role: string; permissions: string[] }>('/permissions/me')
        .then((r) => setPermissions(r.permissions ?? []))
        .catch(() => setPermissions([]))
    } catch {
      setUser(null)
      setPermissions(null)
      setDbReady(true) // no user → the login page must not be gated on the DB
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadUser()
    if (MOCK_MODE) return
    // Hub events fire on signedIn/signedOut/tokenRefresh — keep our user
    // state in sync after Cognito's redirect-back from the hosted UI.
    let unsubscribe: (() => void) | undefined
    void (async () => {
      const { Hub } = await import('aws-amplify/utils')
      unsubscribe = Hub.listen('auth', ({ payload }) => {
        switch (payload.event) {
          case 'signedIn':
          case 'tokenRefresh':
          case 'signInWithRedirect':
            void loadUser()
            break
          case 'signedOut':
            setUser(null)
            setPermissions(null)
            break
          case 'signInWithRedirect_failure':
          case 'tokenRefresh_failure':
            // Stale token / failed redirect — drop the user so RequireAuth
            // bounces them back to /login.
            setUser(null)
            setPermissions(null)
            break
        }
      })
    })()
    return () => { if (unsubscribe) unsubscribe() }
  }, [])

  // Idle timeout (Leilani, 2026-07: "extend the time … after 2 hours if no
  // action it times out"). The Cognito refresh token governs the OUTER limit;
  // this is the activity-based logout — 2h with zero interaction signs the user
  // out. Any real input resets it. Only while signed in, never in mock mode.
  useEffect(() => {
    if (MOCK_MODE || !user) return
    const IDLE_MS = 2 * 60 * 60 * 1000  // 2 hours
    let timer: ReturnType<typeof setTimeout>
    const reset = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        try { sessionStorage.setItem('penser:idle-logout', '1') } catch { /* private mode */ }
        void signOut()
      }, IDLE_MS)
    }
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'visibilitychange'] as const
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    reset()
    return () => {
      clearTimeout(timer)
      events.forEach((e) => window.removeEventListener(e, reset))
    }
  }, [user])

  async function signOut() {
    if (MOCK_MODE) {
      setUser(null)
      setPermissions(null)
      return
    }
    const { signOut: amplifySignOut } = await import('aws-amplify/auth')
    // Plain signOut() — DON'T pass { global: true }. That flag tries to
    // call Cognito's revoke endpoint with the user's access token, which
    // requires the `aws.cognito.signin.user.admin` OAuth scope. Our hosted
    // UI flow only requests `openid email profile`, so the call throws
    // and the local state never gets cleared (signout button appears to
    // do nothing).
    //
    // Plain signOut() with the Cognito hosted UI configured still:
    //   - clears local tokens
    //   - redirects through cognito-domain/logout?client_id=…&logout_uri=…
    //     which invalidates Cognito's session cookie
    //   - bounces back to our /login (which auto-redirects to a fresh
    //     hosted UI login screen)
    // So users can't auto-rehydrate without re-entering credentials.
    // Mark "I just clicked Sign out" so /login can intercept and show a
    // confirmation screen instead of auto-redirecting back to the hosted
    // UI. Without that gate, a federated user signs out → /login auto-fires
    // signInWithRedirect → Cognito has no session → hosted UI → user clicks
    // Continue with Microsoft → MS session cookie still valid → instant
    // re-auth. From the user's perspective, Sign out did nothing.
    //
    // Storing a TIMESTAMP rather than a flag — Cognito's /logout redirect
    // chain can mount /login multiple times before settling, and a
    // read-and-remove pattern only catches the first mount. The timestamp
    // is treated as "valid for 30s" so all those re-mounts see the same
    // signal, and a stale value naturally ages out.
    try {
      sessionStorage.setItem('penser:signed-out-at', String(Date.now()))
    } catch { /* private mode, etc — fine */ }
    try {
      await amplifySignOut()
    } catch (e) {
      // Surface in console for diagnostics, but still clear local state
      // so the user sees the UI flip immediately.
      console.error('[auth] signOut threw', e)
    }
    setUser(null)
    setPermissions(null)
  }

  // Mock mode has no backend to grant against; the mock user is an admin, so
  // treat every capability as granted. Otherwise a key must be in the loaded set.
  function can(key: string): boolean {
    if (MOCK_MODE) return true
    return permissions?.includes(key) ?? false
  }

  async function getAccessToken(): Promise<string | null> {
    if (MOCK_MODE) return 'mock-access-token'
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth')
      const session = await fetchAuthSession()
      return session.tokens?.accessToken?.toString() ?? null
    } catch {
      return null
    }
  }

  return (
    <AuthContext.Provider value={{ user, permissions, can, loading, dbReady, signOut, refresh: loadUser, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
